import { useEffect, useRef, useState } from "react";
import { ApiRequestError, apiRequest } from "../api";
import { AsyncStatus, Button, Shell, TopbarAccountLink, TopbarLoginLink } from "../components";
import { SupportCenter } from "../components/SupportCenter";
import { setPageMetadata } from "../page-metadata";
import type { SessionResponse } from "../types";

const PERSONAL_HOMEPAGE_URL = "https://ruikang.wang/";

function AuthorBio() {
  return (
    <section
      aria-labelledby="contact-author-title"
      className="contact-author"
    >
      <div className="contact-author-heading">
        <span className="card-kicker">作者</span>
        <h2 id="contact-author-title">关于开发者</h2>
      </div>
      <p>
        我是 Ruikang，一名独立开发者，主要做游戏工具、桌面应用和配套服务。CheemsPay 负责这些项目的订单与授权支持。
      </p>
      <a
        className="contact-author-homepage text-link"
        href={PERSONAL_HOMEPAGE_URL}
        rel="noreferrer"
        target="_blank"
      >
        个人主页 ↗
      </a>
    </section>
  );
}

export function ContactPage() {
  const [sessionState, setSessionState] = useState<
    "loading" | "signed-in" | "signed-out" | "error"
  >("loading");
  const [sessionError, setSessionError] = useState("");
  const sessionRequestVersion = useRef(0);

  const loadSession = async () => {
    const requestVersion = ++sessionRequestVersion.current;
    setSessionState("loading");
    setSessionError("");
    try {
      await apiRequest<SessionResponse>("/api/session");
      if (requestVersion === sessionRequestVersion.current) {
        setSessionState("signed-in");
      }
    } catch (caught) {
      if (requestVersion !== sessionRequestVersion.current) {
        return;
      }
      if (caught instanceof ApiRequestError && caught.status === 401) {
        setSessionState("signed-out");
        return;
      }
      setSessionError(
        caught instanceof Error
          ? caught.message
          : "暂时无法确认账户状态。",
      );
      setSessionState("error");
    }
  };

  useEffect(() => {
    setPageMetadata(
      "联系支持 | CheemsPay",
      "联系 CheemsPay 支持、阅读作者简介与项目展示，并在登录后创建客服工单。",
    );
    void loadSession();
    return () => {
      sessionRequestVersion.current += 1;
    };
  }, []);

  if (sessionState === "loading") {
    return (
      <Shell activeItem="contact">
        <section className="contact-page">
          <header className="page-heading">
            <h1>联系支持</h1>
          </header>
          <AsyncStatus tone="loading">正在确认账户状态…</AsyncStatus>
        </section>
      </Shell>
    );
  }

  if (sessionState === "signed-in") {
    return (
      <Shell
        activeItem="contact"
        action={<TopbarAccountLink />}
      >
        <section className="contact-page contact-page-authenticated">
          <header className="page-heading">
            <h1>客服工单</h1>
          </header>
          <SupportCenter />
          <AuthorBio />
        </section>
      </Shell>
    );
  }

  return (
    <Shell
      activeItem="contact"
      action={<TopbarLoginLink returnTo="/contact" />}
    >
      <section className="contact-page">
        <header className="page-heading">
          <h1>联系支持</h1>
          <p>登录后即可在本页创建工单，问题和回复会保存在账户里。</p>
          <a className="button" href="/?access=login&returnTo=%2Fcontact">
            登录后创建工单
          </a>
        </header>

        {sessionState === "error" ? (
          <AsyncStatus
            action={
              <Button
                onClick={() => void loadSession()}
                type="button"
                variant="secondary"
              >
                重试
              </Button>
            }
            tone="warning"
          >
            {sessionError || "暂时无法确认登录状态。"} 仍可浏览本页，登录后才能提交工单。
          </AsyncStatus>
        ) : null}

        <AuthorBio />

        <section
          aria-labelledby="contact-projects-title"
          className="contact-projects"
        >
          <div className="contact-projects-heading">
            <span className="card-kicker">项目</span>
            <h2 id="contact-projects-title">其他作品</h2>
          </div>
          <div className="contact-project-grid">
            <a
              className="contact-project"
              href="https://bomana.ruikang.wang/"
              rel="noreferrer"
              target="_blank"
            >
              <span className="card-kicker">战争雷霆</span>
              <h3>Bomana</h3>
              <p>全真模式计时、导航与飞行辅助。</p>
              <span className="contact-project-visit">访问项目 ↗</span>
            </a>
            <a
              className="contact-project"
              href="https://sigua.ruikang.wang/"
              rel="noreferrer"
              target="_blank"
            >
              <span className="card-kicker">战术小队</span>
              <h3>SiguaArmor</h3>
              <p>相关工具与项目展示。</p>
              <span className="contact-project-visit">访问项目 ↗</span>
            </a>
          </div>
        </section>

        <div className="contact-grid">
          <article className="contact-card dashboard-card">
            <span className="card-kicker">范围</span>
            <h2>可以咨询什么</h2>
            <ul className="contact-list">
              <li>购买前的产品与使用问题</li>
              <li>账号登录与账户访问</li>
              <li>付款、订单与授权状态</li>
              <li>设备授权与产品使用</li>
              <li>退款事项与既有流程说明</li>
            </ul>
          </article>
          <article className="contact-card dashboard-card">
            <span className="card-kicker">流程</span>
            <h2>怎么处理</h2>
            <ol className="contact-list contact-list-ordered">
              <li>登录或注册账户。</li>
              <li>创建工单，写清需要帮助的事项。</li>
              <li>等待回复，不必一直开着页面。</li>
              <li>回来继续说明，结束后关闭工单。</li>
            </ol>
            <p className="contact-muted">工单不是即时聊天，也不保证马上回复。</p>
          </article>
        </div>

        <section className="contact-notes">
          <article className="contact-note safety">
            <span className="card-kicker">安全</span>
            <h2>不要提交秘密信息</h2>
            <p>
              不要在工单里填写密码、恢复暗号、动态口令、备用码、私钥、付款二维码或证件。工单只用于沟通，不能直接办理退款、找回账号或改授权。
            </p>
          </article>
          <article className="contact-note quota">
            <span className="card-kicker">账户</span>
            <h2>登录后即可使用</h2>
            <p>每个账号 24 小时内最多新建 1 条工单，最多同时保留 3 条未关闭工单。</p>
            <p>已开通授权的用户，可以在账户里查看会员讨论群。讨论群不能替代工单。</p>
          </article>
        </section>
      </section>
    </Shell>
  );
}
